import axios from 'axios';
import { API_BASE_URL } from '../../constants/apiConfig';

let csrfToken: string | null = null;
let csrfTokenPromise: Promise<string | null> | null = null;
let authRefreshPromise: Promise<boolean> | null = null;
export const JOBBOARD_UNAUTHORIZED_EVENT = 'jobBoard:unauthorized';
export const MICROSOFT_ACCOUNT_ID_STORAGE_KEY = 'microsoftAccountId';
const MICROSOFT_ACCOUNT_STORAGE_KEY = 'microsoftAccount';

let lastUnauthorizedEventAt = 0;

const emitUnauthorizedEvent = (payload?: { url?: string; method?: string }) => {
  // Avoid spamming redirects when multiple requests fail in a burst.
  const now = Date.now();
  if (now - lastUnauthorizedEventAt < 1000) return;
  lastUnauthorizedEventAt = now;

  try {
    window.dispatchEvent(
      new CustomEvent(JOBBOARD_UNAUTHORIZED_EVENT, { detail: payload })
    );
  } catch {
    // no-op (SSR / unsupported env)
  }
};

export const jobBoardAPI = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
});

const authRefreshClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
});

// Dedicated client without interceptors to obtain a CSRF token.
// Avoids circular imports (auth.ts imports jobBoardAPI) and avoids interceptor recursion.
const csrfClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
});

const extractCsrfToken = (response: any): string | null => {
  // API may return { data: { csrfToken } } (wrapped) or { csrfToken } (raw)
  const payload = response?.data?.data || response?.data;
  return payload?.csrfToken || null;
};

const extractPayload = (response: any) =>
  response?.data?.data || response?.data || null;

const getStoredMicrosoftAccountId = (): string | null => {
  try {
    const directValue = localStorage.getItem(MICROSOFT_ACCOUNT_ID_STORAGE_KEY);
    if (directValue && directValue.trim()) {
      return directValue.trim();
    }

    const legacyValue = localStorage.getItem(MICROSOFT_ACCOUNT_STORAGE_KEY);
    if (!legacyValue) return null;

    const parsed = JSON.parse(legacyValue);
    const accountId = parsed?.homeAccountId;
    return typeof accountId === 'string' && accountId.trim()
      ? accountId.trim()
      : null;
  } catch {
    return null;
  }
};

export const setMicrosoftAccountId = (accountId: string | null) => {
  try {
    if (accountId && accountId.trim()) {
      localStorage.setItem(MICROSOFT_ACCOUNT_ID_STORAGE_KEY, accountId.trim());
      return;
    }

    localStorage.removeItem(MICROSOFT_ACCOUNT_ID_STORAGE_KEY);
  } catch {
    // no-op
  }
};

const isAuthRefreshRequest = (url?: string) =>
  typeof url === 'string' &&
  (url.includes('auth/microsoft-auth/refresh') ||
    url.includes('auth/microsoft-auth/silent-token'));

const tryRefreshAuthSession = async (): Promise<boolean> => {
  if (authRefreshPromise) return authRefreshPromise;

  authRefreshPromise = (async () => {
    const accountId = getStoredMicrosoftAccountId();
    if (!accountId) return false;

    const endpoints = [
      'auth/microsoft-auth/refresh',
      'auth/microsoft-auth/silent-token'
    ];

    for (const endpoint of endpoints) {
      try {
        const response = await authRefreshClient.post(endpoint, { accountId });
        const payload = extractPayload(response);
        const refreshedAccountId = payload?.account?.homeAccountId || accountId;
        setMicrosoftAccountId(refreshedAccountId);
        return true;
      } catch (refreshError: any) {
        const status = refreshError?.response?.status;
        if (status === 404 || status === 405) {
          continue;
        }
      }
    }

    return false;
  })();

  try {
    return await authRefreshPromise;
  } finally {
    authRefreshPromise = null;
  }
};

const refreshCsrfToken = async (): Promise<string | null> => {
  if (csrfTokenPromise) return csrfTokenPromise;
  csrfTokenPromise = (async () => {
    try {
      const response = await csrfClient.get('auth/csrf');
      const token = extractCsrfToken(response);
      csrfToken = token;
      return token;
    } catch (err) {
      csrfToken = null;
      return null;
    } finally {
      csrfTokenPromise = null;
    }
  })();
  return csrfTokenPromise;
};

jobBoardAPI.interceptors.request.use(
  async config => {
    config.headers = config.headers || {};

    const method = (config.method || 'get').toUpperCase();
    const requiresCsrf =
      ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) &&
      !(config.headers as any)['X-CSRF-Token'];

    if (requiresCsrf && !csrfToken) {
      await refreshCsrfToken();
    }

    if (requiresCsrf && csrfToken) {
      (config.headers as any)['X-CSRF-Token'] = csrfToken;
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

jobBoardAPI.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error.response) {
      const { status } = error.response;
      switch (status) {
        case 401: {
          console.warn('Received 401 response from API', originalRequest?.url);
          if (
            originalRequest &&
            !(originalRequest as any)._retryAuth &&
            !isAuthRefreshRequest(originalRequest?.url)
          ) {
            (originalRequest as any)._retryAuth = true;
            const refreshed = await tryRefreshAuthSession();
            if (refreshed) {
              return jobBoardAPI(originalRequest);
            }
          }

          emitUnauthorizedEvent({
            url: originalRequest?.url,
            method: originalRequest?.method
          });
          break;
        }
        case 403: {
          const message =
            error?.response?.data?.message ||
            error?.response?.data?.data?.message ||
            error?.response?.data?.error ||
            '';

          // If CSRF token is missing/stale, refresh and retry once.
          if (
            typeof message === 'string' &&
            message.toLowerCase().includes('csrf') &&
            originalRequest &&
            !(originalRequest as any)._retryCsrf
          ) {
            (originalRequest as any)._retryCsrf = true;
            await refreshCsrfToken();
            if (csrfToken) {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers['X-CSRF-Token'] = csrfToken;
            }
            return jobBoardAPI(originalRequest);
          }

          console.error('Forbidden access');
          break;
        }
        case 400:
          console.error('Bad request');
          break;
        case 404:
          console.error('Not found');
          break;
        default:
          console.error('An error occurred:', error.response.data);
      }
    } else if (error.request) {
      console.error('Network error:', error.request);
    } else {
      console.error('An error occurred:', error.message);
    }
    return Promise.reject(error);
  }
);

export type Query<T> = {
  queryKey: (string | T)[];
};

export interface PaginatedResponse<T> {
  items: T[];
  meta: {
    currentPage: number;
    itemCount: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
  };
}
export const setCsrfToken = (token: string | null) => {
  csrfToken = token;
};

export const getCsrfToken = () => csrfToken;

export default jobBoardAPI;
